import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import Snackbar from '@mui/material/Snackbar';
import Helmet from 'react-helmet';
import { Alert, TextV2 } from '@insights-ltd/design-library/components';
import SelfServiceLayout from 'components/layout/SelfServiceLayout';
import { useForgottenPassword } from 'api';
import { FormData } from 'pages/ForgottenPassword/types';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { Box, styled } from '@mui/material';
import LanguageSelecetWrapper from 'components/LanguageSelectWrapper/LanguageSelectWrapper';

type UserDetailsProps = {
  update: (formData: FormData) => void;
  setShowConfirmation: (showConfirmation: boolean) => void;
  backUrl: string;
};

const StyledH1 = styled(TextV2)(({ theme }) => ({
  marginBottom: theme.spacing(spacingSizeMap.XS),
}));

const UserDetails = ({
  update,
  setShowConfirmation,
  backUrl,
}: UserDetailsProps) => {
  const { t } = useTranslation();
  const { mutate, isPending, isError } = useForgottenPassword();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  const onSubmit = (formData: FormData) =>
    mutate(formData, {
      onSuccess: () => {
        update(formData);
        setShowConfirmation(true);
      },
    });

  return (
    <>
      <Helmet>
        <title>{t('ui.event-management.title.forgotten-password')}</title>
      </Helmet>
      <SelfServiceLayout>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Card
            sx={(theme) => ({
              maxWidth: '31rem',
              padding: `0 ${theme.spacing(spacingSizeMap.XS)}`,
            })}
          >
            <CardContent>
              <Grid
                container
                sx={{
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{
                    alignSelf: 'self-end',
                    width: 'auto',
                  }}
                >
                  <LanguageSelecetWrapper />
                </Box>
                <Grid
                  sx={(theme) => ({
                    flexBasis: 'auto',
                    width: '100%',
                    margin: `${theme.spacing(spacingSizeMap.XS)} 0`,
                  })}
                  size={12}
                >
                  <TextV2 variant="bodyBold">
                    <Link component={RouterLink} to={backUrl}>
                      {t('ui.event-management.forgotten-password.back')}
                    </Link>
                  </TextV2>
                </Grid>
                <Grid
                  sx={(theme) => ({
                    flexBasis: 'auto',
                    width: '100%',
                    margin: `${theme.spacing(spacingSizeMap.XS)} 0`,
                  })}
                  size={12}
                >
                  <StyledH1 component="h1" variant="h3">
                    {t('ui.event-management.forgotten-password.heading')}
                  </StyledH1>
                  <TextV2 variant="body1" color="textSecondary">
                    {t('ui.event-management.forgotten-password.sub-heading')}
                  </TextV2>
                </Grid>
                <Grid
                  sx={(theme) => ({
                    flexBasis: 'auto',
                    width: '100%',
                    margin: `${theme.spacing(spacingSizeMap.XS)} 0`,
                  })}
                  size={12}
                >
                  <FormControl
                    variant="outlined"
                    sx={(theme) => ({
                      flexBasis: 'auto',
                      width: '100%',
                      margin: `${theme.spacing(spacingSizeMap.XS)} 0`,
                    })}
                  >
                    <TextField
                      id="emailAddress"
                      label={t(
                        'ui.event-management.forgotten-password.email.input-label',
                      )}
                      type="email"
                      variant="outlined"
                      error={Boolean(errors.emailAddress)}
                      helperText={
                        errors.emailAddress &&
                        t(
                          'ui.event-management.forgotten-password.email.error.required',
                        )
                      }
                      {...register('emailAddress', { required: true })}
                    />
                  </FormControl>
                </Grid>
                <Grid
                  container
                  sx={[
                    {
                      justifyContent: 'flex-end',
                    },
                    (theme) => ({
                      flexBasis: 'auto',
                      width: '100%',
                      margin: `${theme.spacing(spacingSizeMap.XS)} 0`,
                    }),
                  ]}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    sx={{
                      width: '15rem',
                      height: '3rem',
                    }}
                    disabled={isPending}
                  >
                    {t('ui.event-management.forgotten-password.cta')}
                  </Button>
                </Grid>
              </Grid>
              <Snackbar open={isError} autoHideDuration={6000}>
                <Alert elevation={6} variant="filled" severity="error">
                  {t('ui.event-management.forgotten-password.server.error')}
                </Alert>
              </Snackbar>
            </CardContent>
          </Card>
        </form>
      </SelfServiceLayout>
    </>
  );
};

export default UserDetails;
